<template>
  <a-drawer
    :visible="visible"
    title="비즈메세지 인증 상세"
    :width="1250"
    placement="right"
    :destroy-on-close="true"
    @close="handleClose"
  >
    <div v-if="companyData">
      <!-- 회사 정보 섹션 -->
      <a-divider orientation="left">회사 정보</a-divider>

      <a-descriptions :column="2" bordered size="small" style="margin-bottom: 24px;">
        <a-descriptions-item label="회사명">
          {{ companyData.companyName || '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="회사 ID">
          {{ companyData.companyId || '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="등록일시">
          {{ formatDateTime(companyData.createdAt) }}
        </a-descriptions-item>
        <a-descriptions-item label="수정일시">
          {{ formatDateTime(companyData.updatedAt) }}
        </a-descriptions-item>
        <a-descriptions-item label="수정자" :span="2">
          {{ companyData.updatedBy || '-' }}
        </a-descriptions-item>
      </a-descriptions>

      <!-- 채널 목록 섹션 -->
      <a-divider orientation="left">채널 목록</a-divider>

      <div class="channel-header">
        <div></div>
        <a-button
          type="primary"
          size="small"
          class="yellow-button"
          @click="handleAddChannel"
        >
          <plus-outlined /> 채널 추가
        </a-button>
      </div>

      <a-table
        v-if="companyData.channels && companyData.channels.length > 0"
        class="ant-table-striped"
        size="small"
        :columns="channelColumns"
        :data-source="companyData.channels"
        :pagination="false"
        :row-key="record => record.channelId"
        :scroll="{ x: 800 }"
      >
        <!-- 채널 ID -->
        <template #channelId="{ text }">
          <a-tag color="blue">{{ text }}</a-tag>
        </template>

        <!-- Client ID (마스킹) -->
        <template #clientId="{ text }">
          <span v-if="text">
            ********
            <eye-outlined
              class="view-icon"
              @click="showSecretValue('Client ID', text)"
            />
          </span>
          <span v-else>-</span>
        </template>

        <!-- Client Secret (마스킹) -->
        <template #clientSecret="{ text }">
          <span v-if="text">
            ********
            <eye-outlined
              class="view-icon"
              @click="showSecretValue('Client Secret', text)"
            />
          </span>
          <span v-else>-</span>
        </template>

        <!-- Sender Key (마스킹) -->
        <template #senderKey="{ text }">
          <span v-if="text">
            ********
            <eye-outlined
              class="view-icon"
              @click="showSecretValue('Sender Key', text)"
            />
          </span>
          <span v-else>-</span>
        </template>

        <!-- 토큰 상태 -->
        <template #tokenStatus="{ record }">
          <a-tag :color="getTokenStatusColor(record, companyData)">
            {{ getTokenStatusText(record, companyData) }}
          </a-tag>
        </template>

        <!-- 토큰 만료일 -->
        <template #expiresAt="{ text, record }">
          {{ formatExpiresAt(text, record, companyData) }}
        </template>

        <!-- 관리 토큰 만료일 -->
        <template #managementExpiresAt="{ text }">
          {{ formatDateTime(text) }}
        </template>

        <!-- 작업 -->
        <template #action="{ record }">
          <a-space>
            <a-button
              type="link"
              size="small"
              @click="handleEditChannel(record)"
            >
              수정
            </a-button>
            <a-button
              type="link"
              size="small"
              danger
              @click="handleDeleteChannel(record)"
            >
              삭제
            </a-button>
          </a-space>
        </template>
      </a-table>

      <a-empty v-else description="등록된 채널이 없습니다." style="margin: 40px 0;" />
    </div>

    <!-- 하단 버튼 -->
    <div class="drawer-footer">
      <a-button @click="handleClose">닫기</a-button>
    </div>

    <!-- 채널 추가/수정 드로워 -->
    <ChannelFormDrawer
      v-model:visible="channelFormVisible"
      :channel-data="selectedChannel"
      :mode="channelFormMode"
      @save="handleSaveChannel"
      @close="handleCloseChannelForm"
    />
  </a-drawer>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { message } from 'ant-design-vue';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { BizmsgAuthApi } from '../../api';
import { twoBtnDialog } from '@/utils/utils';
import ChannelFormDrawer from './ChannelFormDrawer.vue';

export default defineComponent({
  name: 'CompanyDetailDrawer',
  components: {
    EyeOutlined,
    PlusOutlined,
    ChannelFormDrawer
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyData: {
      type: Object,
      default: null
    }
  },
  emits: ['update:visible', 'close', 'refresh'],
  setup(props, { emit }) {
    const channelFormVisible = ref(false);
    const channelFormMode = ref('create'); // 'create', 'edit'
    const selectedChannel = ref(null);

    const channelColumns = [
      {
        title: '채널 ID',
        dataIndex: 'channelId',
        key: 'channelId',
        width: 150,
        slots: { customRender: 'channelId' }
      },
      {
        title: '채널명',
        dataIndex: 'channelName',
        key: 'channelName',
        width: 150
      },
      {
        title: 'Client ID',
        dataIndex: 'clientId',
        key: 'clientId',
        width: 120,
        slots: { customRender: 'clientId' }
      },
      {
        title: 'Client Secret',
        dataIndex: 'clientSecret',
        key: 'clientSecret',
        width: 120,
        slots: { customRender: 'clientSecret' }
      },
      {
        title: '발신프로필키',
        dataIndex: 'senderKey',
        key: 'senderKey',
        width: 120,
        slots: { customRender: 'senderKey' }
      },
      {
        title: '토큰 상태',
        key: 'tokenStatus',
        width: 100,
        slots: { customRender: 'tokenStatus' }
      },
      {
        title: '토큰 만료일',
        dataIndex: 'expiresAt',
        key: 'expiresAt',
        width: 170,
        slots: { customRender: 'expiresAt' }
      },
      {
        title: '관리토큰 만료일',
        dataIndex: 'managementExpiresAt',
        key: 'managementExpiresAt',
        width: 170,
        slots: { customRender: 'managementExpiresAt' }
      },
      {
        title: '작업',
        key: 'action',
        width: 120,
        fixed: 'right',
        slots: { customRender: 'action' }
      }
    ];

    // 날짜 포맷팅
    const formatDateTime = (dateStr) => {
      if (!dateStr) return '-';

      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // 특정 회사/채널 체크
    const isSpecialChannel = (channelId, companyId) => {
      return companyId === '64914728bf6d973553eff0ae' && channelId === '@certilife';
    };

    // 토큰 상태 색상
    const getTokenStatusColor = (record, companyData) => {
      // 특정 회사/채널은 관리토큰으로 판단
      const expiresAt = isSpecialChannel(record.channelId, companyData?.companyId)
        ? record.managementExpiresAt
        : record.expiresAt;

      if (!expiresAt) return 'default';

      const now = new Date();
      const expireDate = new Date(expiresAt);
      const diffHours = (expireDate - now) / (1000 * 60 * 60);

      if (diffHours < 0) return 'error'; // 만료됨
      if (diffHours < 1) return 'warning'; // 1시간 미만
      return 'success'; // 정상
    };

    // 토큰 상태 텍스트
    const getTokenStatusText = (record, companyData) => {
      // 특정 회사/채널은 관리토큰으로 판단
      const expiresAt = isSpecialChannel(record.channelId, companyData?.companyId)
        ? record.managementExpiresAt
        : record.expiresAt;

      if (!expiresAt) return '미등록';

      const now = new Date();
      const expireDate = new Date(expiresAt);
      const diffHours = (expireDate - now) / (1000 * 60 * 60);

      if (diffHours < 0) return '만료됨';
      if (diffHours < 1) return '곧 만료';
      return '정상';
    };

    // 토큰 만료일 포맷팅
    const formatExpiresAt = (text, record, companyData) => {
      // 특정 회사/채널은 "아직 deprecated 방식 사용중"으로 표기
      if (isSpecialChannel(record.channelId, companyData?.companyId)) {
        return '아직 deprecated 방식 사용중';
      }
      return formatDateTime(text);
    };

    // 민감 정보 보기
    const showSecretValue = (label, value) => {
      if (!value) {
        message.info(`${label} 값이 없습니다.`);
        return;
      }
      message.info({
        content: `${label}: ${value}`,
        duration: 5
      });
    };

    // 채널 추가
    const handleAddChannel = () => {
      channelFormMode.value = 'create';
      selectedChannel.value = null;
      channelFormVisible.value = true;
    };

    // 채널 수정
    const handleEditChannel = (channel) => {
      channelFormMode.value = 'edit';
      selectedChannel.value = { ...channel };
      channelFormVisible.value = true;
    };

    // 회사 정보 재조회
    const refreshCompanyData = async () => {
      try {
        const updatedData = await BizmsgAuthApi.getBizmsgAuthByCompanyId(props.companyData.companyId);
        emit('refresh', updatedData);
      } catch (error) {
        console.error('회사 정보 재조회 오류:', error);
        message.error('정보를 새로고침하는 중 오류가 발생했습니다.');
      }
    };

    // 채널 삭제
    const handleDeleteChannel = async (channel) => {
      const confirmed = await twoBtnDialog(
        '채널 삭제',
        `"${channel.channelName}" (${channel.channelId}) 채널을 삭제하시겠습니까?`,
        '삭제'
      );

      if (!confirmed) return;

      try {
        await BizmsgAuthApi.deleteChannel(props.companyData.companyId, channel.channelId);
        message.success('채널이 삭제되었습니다.');
        await refreshCompanyData();
      } catch (error) {
        console.error('채널 삭제 오류:', error);
        message.error('채널 삭제 중 오류가 발생했습니다.');
      }
    };

    // 채널 저장
    const handleSaveChannel = async (channelData) => {
      try {
        if (channelFormMode.value === 'create') {
          await BizmsgAuthApi.createChannel(props.companyData.companyId, channelData);
          message.success('채널이 추가되었습니다.');
        } else {
          // 수정 시에는 channelId를 body에서 제거 (URL 경로에 이미 포함됨)
          const { channelId, ...updateData } = channelData;
          await BizmsgAuthApi.updateChannel(props.companyData.companyId, channelId, updateData);
          message.success('채널이 수정되었습니다.');
        }

        channelFormVisible.value = false;
        await refreshCompanyData();
      } catch (error) {
        console.error('채널 저장 오류:', error);
        if (error.response?.status === 409) {
          message.error('이미 존재하는 채널 ID입니다.');
        } else if (error.response?.status === 404) {
          message.error('회사 정보를 찾을 수 없습니다.');
        } else {
          message.error('채널 저장 중 오류가 발생했습니다.');
        }
      }
    };

    // 채널 폼 닫기
    const handleCloseChannelForm = () => {
      channelFormVisible.value = false;
      selectedChannel.value = null;
    };

    // 닫기
    const handleClose = () => {
      emit('update:visible', false);
      emit('close');
    };

    return {
      channelFormVisible,
      channelFormMode,
      selectedChannel,
      channelColumns,
      formatDateTime,
      formatExpiresAt,
      getTokenStatusColor,
      getTokenStatusText,
      showSecretValue,
      handleAddChannel,
      handleEditChannel,
      handleDeleteChannel,
      handleSaveChannel,
      handleCloseChannelForm,
      handleClose
    };
  }
});
</script>

<style scoped>
.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.channel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.view-icon {
  margin-left: 8px;
  cursor: pointer;
  color: #1890ff;
}

.view-icon:hover {
  color: #40a9ff;
}

.yellow-button {
  background-color: #FEE500;
  border-color: #FEE500;
  color: #000000;
}

.yellow-button:hover {
  background-color: #FFD700;
  border-color: #FFD700;
  color: #000000;
}

:deep(.ant-divider-horizontal.ant-divider-with-text-left::before) {
  width: 5%;
}

:deep(.ant-divider-horizontal.ant-divider-with-text-left::after) {
  width: 95%;
}

:deep(.ant-table-striped) :deep(.ant-table-body) tr:nth-child(2n) td {
  background-color: #fafafa;
}
</style>
